import { type FC, useState } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { useZocksAuth, useZocksQuota } from '../../../../components/ScopedBlock/hooks'
import { Spinner } from '../../../../components/Spinner/Spinner'
import { Tooltip } from '../../../../components/Tooltip/Tooltip'
import { promptFileDownload } from '../../../../common/fileUtils'
import { useAppDispatch } from '../../../../store/hooks'
import { addToast } from '../../../../store/toast/toastSlice'
import { VOCABULARY_QUOTA, type VocabularyTerm } from './vocabularyTypes'
import {
  type AddSummary,
  type AddTermsResult,
  buildAddSummary,
  collectRejections,
  describeVocabularyError,
  filterTerms,
  hasSummaryToShow,
  orderFixableTerms,
  validateRename,
} from './vocabularyUtils'
import { useVocabularyTerms } from './useVocabularyTerms'
import { VocabularyAddForm } from './VocabularyAddForm'
import { VocabularyNotice } from './VocabularyNotice'
import { VocabularyTermRow } from './VocabularyTermRow'
import { RemoveTermModal } from './RemoveTermModal'

const EMPTY_STATE_PROMPT = 'Start with client last names, your firm name, and terms your team uses often.'

const buildAddedMessage = (added: number, duplicateCount: number): string => {
  if (duplicateCount === 0) {
    return added === 1 ? '1 term added. It applies from the next meeting.' : `${added} terms added. They apply from the next meeting.`
  }
  const addedPart = added === 1 ? '1 term added.' : `${added} terms added.`
  const duplicatePart =
    duplicateCount === 1 ? '1 was already in your vocabulary or used by Zocks.' : `${duplicateCount} were already in your vocabulary or used by Zocks.`
  return `${addedPart} ${duplicatePart}`
}

export const VocabularySettings: FC = () => {
  const dispatch = useAppDispatch()
  const { hasScope } = useZocksAuth()
  const { hasQuota } = useZocksQuota()
  const {
    accountId,
    accountTerms,
    systemTerms,
    termCount,
    remaining,
    termLimit,
    maxWordsPerTerm,
    maxTermLength,
    isAdding,
    isRenaming,
    isWriting,
    isLoading,
    isError,
    isLocked,
    recentlyAddedIds,
    addTerms,
    renameTerm,
    removeTerm,
  } = useVocabularyTerms()

  const [query, setQuery] = useState('')
  const [isAddFormOpen, setIsAddFormOpen] = useState(false)
  const [addValue, setAddValue] = useState('')
  const [addSubmittedValue, setAddSubmittedValue] = useState<string | null>(null)
  const [addSummary, setAddSummary] = useState<AddSummary | null>(null)
  const [isAddingFromSearch, setIsAddingFromSearch] = useState(false)
  const [editingTermId, setEditingTermId] = useState<string | null>(null)
  const [renameError, setRenameError] = useState<string | null>(null)
  const [termToRemove, setTermToRemove] = useState<VocabularyTerm | null>(null)

  const hasVocabularyQuota = hasQuota(VOCABULARY_QUOTA)
  const canManageAccountConfig = hasScope('**', 'account_config:update', ['account_config:admin'])
  const isReadOnly = isLocked || !hasVocabularyQuota || !canManageAccountConfig
  const isEditingLocked = isReadOnly || isWriting

  const filteredAccountTerms = filterTerms(accountTerms, query)
  const trimmedQuery = query.trim()
  const hasQuery = trimmedQuery.length > 0
  const isCoveredByZocks = systemTerms.some((entry) => entry.term.trim().toLowerCase() === trimmedQuery.toLowerCase())
  const showAccountSection = !hasQuery || filteredAccountTerms.length > 0
  const hasOnlySystemMatch = hasQuery && filteredAccountTerms.length === 0 && isCoveredByZocks
  const hasNoResults = hasQuery && filteredAccountTerms.length === 0 && !isCoveredByZocks
  const showEnterpriseNotice = isLocked && !isLoading
  const showQuotaNotice = !hasVocabularyQuota && !isLoading
  const showPermissionNotice = hasVocabularyQuota && !isLocked && !canManageAccountConfig && !isLoading
  const isAtLimit = remaining === 0
  const isEmptyState = accountTerms.length === 0 && !hasQuery && !isReadOnly

  const showErrorToast = (title: string, error: unknown) => {
    dispatch(addToast({ variant: 'error', title, messages: [describeVocabularyError(error, maxWordsPerTerm)] }))
  }

  const showAddedToast = (count: number, duplicateCount: number) => {
    dispatch(addToast({ variant: 'success', title: 'Vocabulary updated', messages: [buildAddedMessage(count, duplicateCount)] }))
  }

  const openAddForm = () => {
    setAddValue('')
    setAddSubmittedValue(null)
    setAddSummary(null)
    setIsAddFormOpen(true)
  }

  const closeAddForm = () => {
    setAddValue('')
    setAddSubmittedValue(null)
    setAddSummary(null)
    setIsAddFormOpen(false)
  }

  const submitAdd = async () => {
    let result: AddTermsResult
    try {
      result = await addTerms(addValue)
    } catch (error) {
      showErrorToast('Terms not added', error)
      return
    }
    const duplicateCount = collectRejections(result.rejected, 'duplicate').length
    if (result.added > 0) {
      setQuery('')
    }
    if (result.added > 0 && result.rejected.length === duplicateCount) {
      showAddedToast(result.added, duplicateCount)
    }
    const summary = buildAddSummary(result)
    if (!hasSummaryToShow(summary)) {
      closeAddForm()
      return
    }
    const nextValue = orderFixableTerms(result.rejected).join('\n')
    setAddValue(nextValue)
    setAddSubmittedValue(nextValue)
    setAddSummary(summary)
  }

  const handleAddSubmit = () => {
    void submitAdd()
  }

  const handleAddFromSearch = async () => {
    let result: AddTermsResult
    setIsAddingFromSearch(true)
    try {
      result = await addTerms(trimmedQuery)
    } catch (error) {
      showErrorToast('Term not added', error)
      return
    } finally {
      setIsAddingFromSearch(false)
    }
    if (result.added > 0) {
      setQuery('')
      showAddedToast(result.added, 0)
      return
    }
    const reason = result.rejected[0]?.reason
    const message =
      reason === 'tooManyWords'
        ? `A term can have at most ${maxWordsPerTerm} words.`
        : reason === 'termTooLong'
          ? `A term can have at most ${maxTermLength} characters.`
          : reason === 'duplicate'
            ? 'That term is already in the vocabulary.'
            : `The ${termLimit}-term limit has been reached. Remove a term to make room.`
    dispatch(addToast({ variant: 'error', title: 'Term not added', messages: [message] }))
  }

  const handleExport = async () => {
    const contents = JSON.stringify({ terms: accountTerms.map((entry) => entry.term) }, null, 2)
    await promptFileDownload(new Blob([contents], { type: 'application/json' }), `zocks-account-${accountId}-vocabulary.json`)
  }

  const startEditing = (term: VocabularyTerm) => {
    setEditingTermId(term.id)
    setRenameError(null)
  }

  const cancelEditing = () => {
    setEditingTermId(null)
    setRenameError(null)
  }

  const submitRename = async (term: VocabularyTerm, newTerm: string) => {
    const trimmed = newTerm.trim()
    const validationError = validateRename(trimmed, maxWordsPerTerm)
    if (validationError) {
      setRenameError(validationError)
      return
    }
    try {
      await renameTerm(term.id, trimmed)
    } catch (error) {
      setRenameError(describeVocabularyError(error, maxWordsPerTerm))
      return
    }
    cancelEditing()
  }

  const handleRename = (term: VocabularyTerm, newTerm: string) => {
    void submitRename(term, newTerm)
  }

  const handleConfirmRemove = async (term: VocabularyTerm) => {
    setTermToRemove(null)
    try {
      await removeTerm(term.id)
    } catch (error) {
      showErrorToast('Term not removed', error)
      return
    }
    dispatch(addToast({ variant: 'success', title: 'Vocabulary updated', messages: [`Removed "${term.term}"`] }))
  }

  const addFormProps = {
    value: addValue,
    submittedValue: addSubmittedValue,
    summary: addSummary,
    isSubmitting: isAdding,
    remaining,
    termLimit,
    maxWordsPerTerm,
    maxTermLength,
    onChange: setAddValue,
    onSubmit: handleAddSubmit,
    onClose: closeAddForm,
  }

  return (
    <Container fluid className='p-0 d-flex flex-column gap-4'>
      <Row>
        <Col className='d-flex flex-column zocks-gap-0.5'>
          <h3 className='zocks-text-gray-900 mb-0'>Vocabulary</h3>
          <p className='paragraph-medium zocks-text-gray-700 mb-0'>
            Client names and industry terms are easy to mishear. Add the terms your team uses most so the AI Assistant is more likely to get them right. New
            terms apply from the next meeting.
          </p>
        </Col>
      </Row>

      {showEnterpriseNotice ? (
        <VocabularyNotice icon='bi-buildings'>Custom terms are managed at the enterprise level. They can't be modified here.</VocabularyNotice>
      ) : null}
      {showQuotaNotice ? (
        <VocabularyNotice icon='bi-lock'>Custom vocabulary is not included in your current plan. Contact us to enable it.</VocabularyNotice>
      ) : null}
      {showPermissionNotice ? (
        <VocabularyNotice icon='bi-person'>Custom terms are managed by your account admins. They can't be modified here.</VocabularyNotice>
      ) : null}

      {isLoading ? (
        <div className='d-flex align-items-center justify-content-center py-5'>
          <Spinner />
        </div>
      ) : isError ? (
        <div className='d-flex flex-column align-items-center text-center zocks-gap-0.75 py-5 paragraph-medium zocks-text-gray-700' role='alert'>
          The vocabulary could not be loaded. Refresh the page to try again.
        </div>
      ) : isEmptyState ? (
        <VocabularyAddForm {...addFormProps} hideCancel prompt={EMPTY_STATE_PROMPT} />
      ) : (
        <div className='d-flex flex-column gap-3'>
          <Row className='align-items-center g-3'>
            <Col xs={12} md={5}>
              <div className='d-flex align-items-center zocks-gap-1.25 border rounded-1 zocks-border-color-gray-400 zocks-px-0.75'>
                <i className='bi bi-search zocks-text-gray-500' aria-hidden='true' />
                <Form.Control
                  type='search'
                  autoComplete='off'
                  value={query}
                  onChange={(event) => setQuery(event.currentTarget.value)}
                  placeholder='Search vocabulary'
                  aria-label='Search vocabulary'
                  className='border-0 px-0 paragraph-small'
                />
              </div>
            </Col>
            <Col className='d-flex justify-content-end'>
              {isAddFormOpen ? null : (
                <Tooltip title={isAtLimit ? `You've reached the ${termLimit}-term limit. Remove terms to add new ones.` : undefined} placement='top'>
                  <Button type='button' variant='primary' disabled={isEditingLocked || isAtLimit} onClick={openAddForm}>
                    Add terms
                  </Button>
                </Tooltip>
              )}
            </Col>
          </Row>

          {isAddFormOpen ? <VocabularyAddForm {...addFormProps} /> : null}

          {hasNoResults ? (
            <div className='d-flex flex-column align-items-center text-center zocks-gap-0.75 py-5'>
              <div className='paragraph-medium zocks-text-gray-700'>No terms match "{trimmedQuery}".</div>
              <Button
                type='button'
                variant='secondaryGhostButton'
                loading={isAddingFromSearch}
                disabled={isEditingLocked || isAtLimit}
                onClick={() => void handleAddFromSearch()}
              >
                Add as custom term
              </Button>
            </div>
          ) : null}

          {hasOnlySystemMatch ? (
            <div className='d-flex justify-content-center text-center py-5'>
              <div className='paragraph-medium zocks-text-gray-700'>"{trimmedQuery}" is already in your vocabulary or used by Zocks.</div>
            </div>
          ) : null}

          {showAccountSection ? (
            <section className='border zocks-border-color-gray-400 rounded-3' aria-label='Custom terms'>
              <div className='d-flex align-items-center justify-content-between gap-3 zocks-px-0.75 py-3'>
                <div className='lead-medium text-uppercase zocks-text-gray-900'>{isLocked ? 'Enterprise terms' : 'Custom terms'}</div>
                <div className='d-flex align-items-center zocks-gap-0.75 flex-shrink-0'>
                  {isLocked ? null : (
                    <div className='paragraph-small zocks-text-gray-600 text-nowrap' aria-live='polite'>
                      {termCount} of {termLimit} terms
                    </div>
                  )}
                  <Tooltip title='Export list to file' placement='top'>
                    <button
                      type='button'
                      className='btn p-0 border-0 d-flex zocks-text-gray-900'
                      aria-label='Export list to file'
                      onClick={() => void handleExport()}
                    >
                      <i className='bi bi-download' aria-hidden='true' />
                    </button>
                  </Tooltip>
                </div>
              </div>
              <ul className='list-unstyled mb-0 border-top zocks-border-color-gray-400' aria-label='Custom terms'>
                {filteredAccountTerms.map((term, index) => (
                  <VocabularyTermRow
                    key={term.id}
                    term={term}
                    isLast={index === filteredAccountTerms.length - 1}
                    isEditing={editingTermId === term.id}
                    isSaving={isRenaming && editingTermId === term.id}
                    error={editingTermId === term.id ? renameError : null}
                    readOnly={isReadOnly}
                    busy={isWriting}
                    justAdded={recentlyAddedIds.includes(term.id)}
                    onEdit={startEditing}
                    onCancelEdit={cancelEditing}
                    onRename={handleRename}
                    onRemove={setTermToRemove}
                  />
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}

      {termToRemove ? <RemoveTermModal term={termToRemove} onHide={() => setTermToRemove(null)} onConfirm={(term) => void handleConfirmRemove(term)} /> : null}
    </Container>
  )
}
